import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Upload, FileUp, Download, ChevronDown } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { paidByInvoice, studentSummary, groupBy } from '../../lib/fees';
import { formatRM } from '../../lib/format';
import PageHeader from '../../components/PageHeader';
import { TableSkeleton } from '../../components/Skeletons';
import DataTable from '../../components/DataTable';
import StatusPill from '../../components/StatusPill';
import UserAvatar from '../../components/UserAvatar';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import StudentImportDialog from '../../components/StudentImportDialog';
import { useAuth } from '../../context/AuthContext';

export default function Pelajar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { school } = useAuth();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [importOpen, setImportOpen] = useState(false);

  const loadRows = async () => {
    const [{ data: students }, { data: classes }, { data: groups }, { data: invoices }, { data: payments }] = await Promise.all([
      supabase.from('students').select('id, nama, no_pelajar, class_id, group_id, aktif').order('nama'),
      supabase.from('classes').select('id, nama'),
      supabase.from('groups').select('id, nama').then((r) => ({ data: r.error ? [] : r.data })),
      supabase.from('invoices').select('id, student_id, jumlah_bayar_rm, status'),
      supabase.from('payments').select('id, invoice_id, jumlah_rm, voided'),
    ]);
    const paidMap = paidByInvoice(payments || []);
    const classMap = Object.fromEntries((classes || []).map((c) => [c.id, c.nama]));
    const groupMap = Object.fromEntries((groups || []).map((g) => [g.id, g.nama]));
    const invByStudent = groupBy(invoices || [], 'student_id');
    const built = (students || []).map((s) => {
      const sum = studentSummary(invByStudent[s.id] || [], paidMap);
      return { ...s, kelas: classMap[s.class_id] || '-', grup: groupMap[s.group_id] || '-', ...sum };
    });
    setRows(built);
    setLoading(false);
  };

  useEffect(() => { loadRows(); }, []);

  const tableConfig = useMemo(() => ({
    tabs: [
      { key: 'semua', label: t('semua') },
      { key: 'aktif', label: t('tabAktif'), filter: (r) => r.aktif },
      { key: 'tidak', label: t('tabTidakAktif'), filter: (r) => !r.aktif },
    ],
    statusOptions: [
      { key: 'belum', label: t('stBelum'), filter: (r) => r.status === 'belum' },
      { key: 'separa', label: t('stSepara'), filter: (r) => r.status === 'separa' },
      { key: 'lunas', label: t('stLunas'), filter: (r) => r.status === 'lunas' },
    ],
    columns: [
      {
        key: 'nama', header: t('thPelajar'), sortable: true, sortValue: (r) => r.nama,
        render: (r) => (
          <div className="flex items-center gap-3 min-w-0">
            <UserAvatar nama={r.nama} size={38} />
            <div className="min-w-0">
              <p className="font-semibold text-foreground truncate">{r.nama}</p>
              <p className="text-xs text-muted-foreground">{r.no_pelajar}</p>
            </div>
          </div>
        ),
      },
      { key: 'kelas', header: t('thKelas'), sortable: true, sortValue: (r) => r.kelas, render: (r) => <span className="text-muted-foreground">{r.kelas}</span> },
      { key: 'grup', header: t('grup'), sortable: true, sortValue: (r) => r.grup, render: (r) => <span className="text-muted-foreground">{r.grup}</span> },
      { key: 'status', header: t('thStatus'), render: (r) => <StatusPill status={r.status} /> },
      {
        key: 'tunggakan', header: t('thTunggakan'), align: 'right', sortable: true, sortValue: (r) => r.outstanding,
        render: (r) => (
          <span className={`tabular font-mono font-bold ${r.outstanding > 0 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}`}>
            {formatRM(r.outstanding)}
          </span>
        ),
      },
    ],
  }), [t]);

  return (
    <div>
      <PageHeader
        title={t('pelajarKelas')}
        subtitle={t('pelajarBerdaftar', { n: rows.length })}
        breadcrumbs={[{ label: t('pengurusan') }, { label: t('pelajarKelas') }]}
        actions={(
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="h-11 bg-brand text-white hover:bg-brand-royal" data-testid="import-menu">
                <Upload className="h-4 w-4 mr-2" />{t('import')}
                <ChevronDown className="h-4 w-4 ml-1.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuItem onSelect={() => setImportOpen(true)} data-testid="import-input-data" className="cursor-pointer">
                <FileUp className="h-4 w-4 mr-2 text-brand" />
                <span className="font-semibold">{t('inputData')}</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs">{t('muatTurunTemplate')}</DropdownMenuLabel>
              <DropdownMenuItem asChild className="cursor-pointer">
                <a href="/templates/template-import-pelajar.xlsx" download data-testid="import-template-xlsx">
                  <Download className="h-4 w-4 mr-2 text-emerald-600 dark:text-emerald-400" />{t('templateExcel')}
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild className="cursor-pointer">
                <a href="/templates/template-import-pelajar.csv" download data-testid="import-template-csv">
                  <Download className="h-4 w-4 mr-2 text-emerald-600 dark:text-emerald-400" />{t('templateCsv')}
                </a>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      />

      <StudentImportDialog open={importOpen} onOpenChange={setImportOpen} school={school} onDone={loadRows} />

      {loading ? <TableSkeleton /> : (
        <DataTable
          testid="pelajar"
          rows={rows}
          tabs={tableConfig.tabs}
          searchKeys={[(r) => r.nama, (r) => r.kelas, (r) => r.grup, (r) => r.no_pelajar]}
          searchPlaceholder={t('cariPelajarPh')}
          statusOptions={tableConfig.statusOptions}
          columns={tableConfig.columns}
          onRowClick={(r) => navigate(`/bendahara/pelajar/${r.id}`)}
          rowTestId={(r) => `pelajar-row-${r.id}`}
          empty={{ title: t('tiadaPelajarDijumpai'), description: t('cubaKataKunci') }}
        />
      )}
    </div>
  );
}
